import { Link } from 'react-router-dom';
import { useAppStore } from '../stores/appStore';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '../components/ui';
import {
  Bot,
  GitBranch,
  Link2,
  MessageSquare,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { motion } from 'framer-motion';

export function Dashboard() {
  const { stats, connections, bots, flows, isLoading } = useAppStore();

  // Data is fetched in App.tsx
  const botResolvedThisMonth = stats?.botResolvedThisMonth || 0;
  const transferredToHumanThisMonth = stats?.transferredToHumanThisMonth || 0;
  const resolvedPool = botResolvedThisMonth + transferredToHumanThisMonth;
  const botResolutionRateThisMonth = typeof stats?.botResolutionRateThisMonth === 'number'
    ? stats.botResolutionRateThisMonth
    : (resolvedPool > 0 ? Math.round((botResolvedThisMonth * 100) / resolvedPool) : 0);


  const statsCards = [
    {
      title: 'Conexões Ativas',
      value: stats?.connections || connections.filter((c) => c.status === 'active').length,
      icon: Link2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Bots Ativos',
      value: stats?.activeBots || bots.filter((b) => b.status === 'active').length,
      icon: Bot,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Fluxos Publicados',
      value: stats?.publishedFlows || flows.filter((f) => f.status === 'published').length,
      icon: GitBranch,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Resolvidas pelo bot (Mês)',
      value: botResolutionRateThisMonth,
      suffix: '%',
      helperText: `${botResolvedThisMonth} de ${resolvedPool} conversas sem transferência`,
      hint: 'Percentual de conversas concluídas pelo bot sem transferência para humano no mês atual.',
      icon: MessageSquare,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-64 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="h-10 w-32 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-8 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="h-6 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((__, rowIndex) => (
                  <div key={rowIndex} className="h-14 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="space-y-6"
    >
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">Visão geral do seu chatbot</p>
        </div>
        <div className="flex gap-3">
          <Link to="/flows/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Novo Fluxo
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.24, delay: index * 0.04, ease: 'easeOut' }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500" title={stat.hint || ''}>
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                      {stat.value}
                      {stat.suffix && (
                        <span className="text-sm font-normal text-slate-500 dark:text-slate-300">
                          {stat.suffix}
                        </span>
                      )}
                    </p>
                    {stat.helperText && (
                      <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
                        {stat.helperText}
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bots */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Bots Recentes</CardTitle>
            <Link to="/bots">
              <Button variant="ghost" size="sm">
                Ver todos
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {bots.length === 0 ? (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 text-slate-300 dark:text-slate-500 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-300">Nenhum bot criado ainda</p>
                <Link to="/bots">
                  <Button variant="outline" size="sm" className="mt-3">
                    Criar primeiro bot
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bots.slice(0, 5).map((bot) => (
                  <div
                    key={bot.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Bot className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{bot.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-300">{bot.code}</p>
                      </div>
                    </div>
                    <Badge
                      variant={bot.status === 'active' ? 'success' : 'default'}
                    >
                      {bot.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Flows */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Fluxos Recentes</CardTitle>
            <Link to="/flows">
              <Button variant="ghost" size="sm">
                Ver todos
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {flows.length === 0 ? (
              <div className="text-center py-8">
                <GitBranch className="w-12 h-12 text-slate-300 dark:text-slate-500 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-300">Nenhum fluxo criado ainda</p>
                <Link to="/flows/new">
                  <Button variant="outline" size="sm" className="mt-3">
                    Criar primeiro fluxo
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {flows.slice(0, 5).map((flow) => (
                  <Link
                    key={flow.id}
                    to={`/flows/${flow.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <GitBranch className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{flow.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          {flow.nodes?.length || 0} nós
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        flow.status === 'published'
                          ? 'success'
                          : flow.status === 'draft'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {flow.status === 'published'
                        ? 'Publicado'
                        : flow.status === 'draft'
                        ? 'Rascunho'
                        : 'Arquivado'}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {connections.some((c) => c.status === 'error') && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">
                  Algumas conexões estão com erro
                </p>
                <p className="text-sm text-red-700">
                  Verifique suas conexões com o Bitrix24 para garantir o funcionamento dos bots.
                </p>
              </div>
              <Link to="/connections" className="ml-auto">
                <Button variant="outline" size="sm">
                  Ver conexões
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
